"use client";

import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useStore } from "@/lib/store";
import { STATUSES } from "@/lib/constants";
import type { Issue, StatusId } from "@/lib/types";
import { IssueRow } from "./IssueRow";
import { StatusIcon } from "@/components/ui/StatusIcon";

export function ListView({ issues, canWrite }: { issues: Issue[]; canWrite: boolean }) {
  const selectedIssueId = useStore((s) => s.selectedIssueId);
  const setCreateOpen = useStore((s) => s.setCreateOpen);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({
    canceled: true,
  });

  const byStatus = useMemo(() => {
    const grouped: Record<StatusId, Issue[]> = {
      backlog: [],
      todo: [],
      in_progress: [],
      in_review: [],
      done: [],
      canceled: [],
    };
    for (const i of issues) grouped[i.status].push(i);
    return grouped;
  }, [issues]);

  if (issues.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[var(--bg-tertiary)] border border-[var(--border)] flex items-center justify-center">
            <Plus size={20} className="text-[var(--fg-tertiary)]" />
          </div>
          <h3 className="text-[15px] font-semibold mb-1">No tasks here yet</h3>
          <p className="text-[13px] text-[var(--fg-tertiary)] mb-4">
            {canWrite ? (
              <>
                Create your first task to start tracking work. Press{" "}
                <kbd className="kbd">C</kbd> anywhere.
              </>
            ) : (
              "There are no tasks in this view."
            )}
          </p>
          {canWrite && (
            <button
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[12.5px] font-medium transition-colors"
            >
              <Plus size={13} />
              New task
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto scrollbar-thin">
      {STATUSES.map((s) => {
        const list = byStatus[s.id];
        if (!list.length) return null;
        const isCollapsed = collapsed[s.id];
        return (
          <div key={s.id} className="">
            <button
              onClick={() => setCollapsed((c) => ({ ...c, [s.id]: !c[s.id] }))}
              className="w-full flex items-center gap-2 px-5 py-1.5 bg-[var(--bg-secondary)] border-b border-[var(--border)] hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              <span className="text-[var(--fg-tertiary)]">
                {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
              </span>
              <StatusIcon status={s.id} size={13} />
              <span className="text-[12.5px] font-medium text-[var(--fg)]">{s.name}</span>
              <span className="text-[11.5px] text-[var(--fg-muted)]">{list.length}</span>
              {canWrite && (
                <span
                  className="ml-auto w-5 h-5 rounded hover:bg-[var(--bg-hover)] flex items-center justify-center text-[var(--fg-tertiary)] transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreateOpen(true);
                  }}
                  role="button"
                  tabIndex={0}
                >
                  <Plus size={12} />
                </span>
              )}
            </button>
            <AnimatePresence initial={false}>
              {!isCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="overflow-hidden"
                >
                  {list.map((i) => (
                    <IssueRow key={i.id} issue={i} active={selectedIssueId === i.id} />
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
}
